// pages/Forbidden.jsx
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  useTheme,
  alpha
} from '@mui/material';
import {
  BlockRounded,
  HomeRounded,
  LockRounded
} from '@mui/icons-material';

const Forbidden = () => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isArabic = i18n.language === 'ar';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.error.light, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
        py: 4,
        px: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '5px',
              background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            }
          }}
        >
          {/* Icon Section */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 3 }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
                position: 'relative',
                mb: 2
              }}
            >
              <LockRounded
                sx={{
                  fontSize: 40,
                  color: 'error.main',
                }}
              />
              <BlockRounded
                sx={{
                  fontSize: 28,
                  color: 'error.main',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  opacity: 0.3
                }}
              />
            </Box>
          </Stack>

          {/* Error Code */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '4rem', sm: '5rem' },
              fontWeight: 800,
              color: 'error.main',
              mb: 1,
              lineHeight: 1,
              letterSpacing: '-0.02em'
            }}
          >
            403
          </Typography>

          {/* Main Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}
          >
            {isArabic ? 'ممنوع الوصول' : 'Access Forbidden'}
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 1,
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              lineHeight: 1.6
            }}
          >
            {isArabic
              ? 'عذراً، ليس لديك صلاحية للوصول إلى هذه الصفحة'
              : "Sorry, you don't have permission to access this page"}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
              mb: 4,
              fontSize: '0.9rem'
            }}
          >
            {isArabic
              ? 'يرجى الاتصال بالمسؤول إذا كنت تعتقد أن هذا خطأ'
              : 'Please contact the administrator if you think this is a mistake'}
          </Typography>

          {/* Action Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            <Button
              component={Link}
              to="/"
              variant="contained"
              size="large"
              startIcon={<HomeRounded />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: theme.shadows[4],
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                '&:hover': {
                  boxShadow: theme.shadows[8],
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              {isArabic ? 'العودة للرئيسية' : 'Back to Home'}
            </Button>

            <Button
              component={Link}
              to={-1}
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              {isArabic ? 'الرجوع للخلف' : 'Go Back'}
            </Button>
          </Stack>

          {/* Additional Info */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                display: 'block',
                fontSize: '0.85rem'
              }}
            >
              {isArabic
                ? 'رمز الخطأ: 403 - Forbidden'
                : 'Error Code: 403 - Forbidden'}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Forbidden;